import { Image } from 'expo-image';
import React, { useEffect, useRef, useState } from 'react';
import {
    Dimensions,
    Pressable,
    StyleSheet,
    View,
    type StyleProp,
    type ViewStyle,
} from 'react-native';

import { formatUaePassTitle } from '@/components/atoms/UAEPassButton';
import { uaePassLoginButtonFallback } from '@/components/molecules/uaePassLoginButtonFallback';
import { useContentfulRevision, useTranslation } from '@/core/hooks';
import {
    fetchUaePassLoginButtonImage,
    type UaePassLoginButtonImage,
} from '@/core/services/contentfulClient';

/** UAE PASS guideline size on mobile: 90% of the window width minus 48, always 54 high. */
const UAE_PASS_BUTTON_HEIGHT = 54;
const DISABLED_OPACITY = 0.5;
const PRESSED_OPACITY = 0.8;

export interface UAEPassLoginButtonProps {
    onPress?: () => void;
    disabled?: boolean;
    style?: StyleProp<ViewStyle>;
    testID?: string;
}

/**
 * "Login with UAE PASS" per the UAE PASS guidelines, matching web's LoginDialog:
 * the official localized artwork from Contentful (`uaePassLoginButton`), or the
 * bundled English or Arabic artwork when Contentful has none or its image fails to load.
 * A same-size placeholder holds the space while Contentful resolves.
 */
export function UAEPassLoginButton({
    onPress,
    disabled = false,
    style,
    testID,
}: Readonly<UAEPassLoginButtonProps>) {
    const { t, currentLanguage } = useTranslation();
    const { width: screenWidth } = Dimensions.get('window');
    const uaePassButtonWidth = screenWidth * 0.9 - 48;
    const sizeStyle = { width: uaePassButtonWidth };
    const [image, setImage] = useState<UaePassLoginButtonImage | null>(null);
    const [isResolved, setIsResolved] = useState(false);
    const [failedUrl, setFailedUrl] = useState<string | null>(null);
    const title = t('auth.loginWithUAEPass');

    // Re-fetch the artwork after an app-wide Contentful refresh (app foreground).
    const contentfulRevision = useContentfulRevision();
    const loadedLanguageRef = useRef<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        // A language change swaps the artwork, so hold the placeholder; a plain
        // refresh keeps the current button on screen until the new one arrives.
        const isRefresh = loadedLanguageRef.current === currentLanguage;
        if (!isRefresh) {
            setIsResolved(false);
            setFailedUrl(null);
        }
        void fetchUaePassLoginButtonImage(currentLanguage === 'ar' ? 'ar' : 'en')
            .catch(() => null)
            .then((result) => {
                if (cancelled) {
                    return;
                }
                if (result || !isRefresh) {
                    setImage(result);
                }
                loadedLanguageRef.current = currentLanguage;
                setIsResolved(true);
            });
        return () => {
            cancelled = true;
        };
    }, [currentLanguage, contentfulRevision]);

    const remoteUrl = image && image.url !== failedUrl ? image.url : null;

    if (!remoteUrl && !isResolved) {
        return <View style={[styles.sized, sizeStyle, style]} />;
    }

    return (
        <Pressable
            onPress={onPress}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityLabel={formatUaePassTitle(title)}
            accessibilityState={{ disabled }}
            testID={testID}
            style={({ pressed }) => [
                styles.sized,
                sizeStyle,
                pressed && !disabled && styles.pressed,
                disabled && styles.disabled,
                style,
            ]}
        >
            <Image
                source={remoteUrl ? { uri: remoteUrl } : uaePassLoginButtonFallback(currentLanguage)}
                style={styles.image}
                contentFit="contain"
                accessibilityIgnoresInvertColors
                {...(remoteUrl ? { onError: () => setFailedUrl(remoteUrl) } : {})}
            />
        </Pressable>
    );
}

const styles = StyleSheet.create({
    sized: {
        height: UAE_PASS_BUTTON_HEIGHT,
        maxWidth: '100%',
        alignSelf: 'center',
    },
    image: {
        width: '100%',
        height: '100%',
    },
    pressed: {
        opacity: PRESSED_OPACITY,
    },
    disabled: {
        opacity: DISABLED_OPACITY,
    },
});

export default UAEPassLoginButton;
